import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import {
  useSendMessageMutation,
  useGetClassesQuery,
  useLocalGetLocationsQuery,
} from '../../store/api/localApi';
import { MessageType, MessageTargetType, Location, Class } from '../../types';
import { useToast } from '../../components/Toast';
import StudentPicker from '../../components/forms/StudentPicker';
import ConfirmDialog from '../../components/ConfirmDialog';

// ── ComposeAnnouncementForm ─────────────────────────────────────────────────
//
// Admin/staff compose form for sending announcements to org, location, class,
// or specific students. Target options are gated by role both client-side
// (UI hides what the user can't pick) and server-side (the gate of record).

const ALL_TARGETS = [
  { value: MessageTargetType.ORG, label: 'Entire organisation' },
  { value: MessageTargetType.LOCATION, label: 'Location' },
  { value: MessageTargetType.CLASS, label: 'Class' },
  { value: MessageTargetType.STUDENTS, label: 'Specific students' },
] as const;

/**
 * Match the backend permission gate in create-message/index.ts:
 *   ORG       — SUPER_ADMIN, ORG_ADMIN
 *   LOCATION  — SUPER_ADMIN, ORG_ADMIN, LOCATION_ADMIN
 *   CLASS     — SUPER_ADMIN, ORG_ADMIN, LOCATION_ADMIN, STAFF
 *   STUDENTS  — SUPER_ADMIN, ORG_ADMIN, LOCATION_ADMIN, STAFF
 */
function getVisibleTargets(flags: {
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  isLocationAdmin: boolean;
  isStaff: boolean;
}): (typeof ALL_TARGETS)[number][] {
  if (flags.isSuperAdmin || flags.isOrgAdmin) return [...ALL_TARGETS];
  if (flags.isLocationAdmin) return ALL_TARGETS.filter((t) => t.value !== MessageTargetType.ORG);
  if (flags.isStaff)
    return ALL_TARGETS.filter(
      (t) => t.value === MessageTargetType.CLASS || t.value === MessageTargetType.STUDENTS
    );
  return [];
}

const ComposeAnnouncementForm: React.FC = () => {
  const {
    activeOrganizationId,
    activeOrganizationName,
    isOrgLevel,
    isSuperAdmin,
    isOrgAdmin,
    isLocationAdmin,
    isStaff,
  } = useAuth();
  const visibleTargets = useMemo(
    () => getVisibleTargets({ isSuperAdmin, isOrgAdmin, isLocationAdmin, isStaff }),
    [isSuperAdmin, isOrgAdmin, isLocationAdmin, isStaff]
  );
  const { effectiveLocationId, activeLocationName, effectiveOrgId } = useLocationContext();

  const { showSuccess, showError } = useToast();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  // Form state — initialize to the first target the user can actually pick
  // for their role; the effect below realigns it if the role changes.
  const [targetType, setTargetType] = useState<MessageTargetType>(
    visibleTargets[0]?.value ?? MessageTargetType.CLASS
  );
  useEffect(() => {
    if (!visibleTargets.some((t) => t.value === targetType)) {
      const next = visibleTargets[0]?.value;
      if (next) setTargetType(next);
    }
  }, [visibleTargets, targetType]);
  // For LOCATION target: which location (preselect active if available)
  const [selectedLocationId, setSelectedLocationId] = useState<string>(effectiveLocationId || '');
  // For CLASS target: which class
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  // For STUDENTS target: which students
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  // Message fields
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [repliesAllowed, setRepliesAllowed] = useState(false);

  // When target changes reset sub-selections
  const handleTargetChange = (next: MessageTargetType) => {
    setTargetType(next);
    setSelectedClassId('');
    setSelectedStudentIds([]);
    if (next === MessageTargetType.LOCATION && effectiveLocationId) {
      setSelectedLocationId(effectiveLocationId);
    }
  };

  // Determine which locationId to use for class/student pickers
  const pickerLocationId =
    targetType === MessageTargetType.LOCATION
      ? selectedLocationId
      : targetType === MessageTargetType.CLASS || targetType === MessageTargetType.STUDENTS
      ? selectedLocationId || effectiveLocationId || ''
      : '';

  // Load locations for org-level users (ORG_ADMIN, SUPER_ADMIN)
  const orgIdForLocations = effectiveOrgId || activeOrganizationId;
  const { data: locations = [] } = useLocalGetLocationsQuery(orgIdForLocations || '', {
    skip: !isOrgLevel || !orgIdForLocations,
  });

  // Load classes when CLASS target is selected
  const { data: classes = [] } = useGetClassesQuery(pickerLocationId, {
    skip: targetType !== MessageTargetType.CLASS || !pickerLocationId,
  });

  // ── Target description for confirm dialog ────────────────────────────────
  const buildTargetDescription = (): string => {
    switch (targetType) {
      case MessageTargetType.ORG:
        return `all members of ${activeOrganizationName || 'this organisation'}`;
      case MessageTargetType.LOCATION: {
        if (isOrgLevel) {
          const loc = locations.find((l: Location) => l.id === selectedLocationId);
          return loc ? `all members at ${loc.name}` : 'the selected location';
        }
        return `all members at ${activeLocationName || 'this location'}`;
      }
      case MessageTargetType.CLASS: {
        const cls = classes.find((c: Class) => c.id === selectedClassId);
        return cls ? `all parents in ${cls.name}` : 'the selected class';
      }
      case MessageTargetType.STUDENTS:
        return `${selectedStudentIds.length} selected student${
          selectedStudentIds.length === 1 ? '' : 's'
        }' parents`;
      default:
        return 'the selected recipients';
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required.';
    if (!body.trim()) return 'Body is required.';
    if (targetType === MessageTargetType.LOCATION && !selectedLocationId)
      return 'Please select a location.';
    if (targetType === MessageTargetType.CLASS && !selectedClassId) return 'Please select a class.';
    if (targetType === MessageTargetType.STUDENTS && selectedStudentIds.length === 0)
      return 'Please select at least one student.';
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      showError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    try {
      const payload: Parameters<typeof sendMessage>[0] = {
        type: MessageType.ANNOUNCEMENT,
        title: title.trim(),
        body: body.trim(),
        targetType,
        repliesAllowed,
        ...(targetType === MessageTargetType.ORG && {
          targetOrgId: effectiveOrgId || activeOrganizationId || undefined,
        }),
        ...(targetType === MessageTargetType.LOCATION && {
          targetLocationId: selectedLocationId || effectiveLocationId || undefined,
        }),
        ...(targetType === MessageTargetType.CLASS && {
          targetLocationId: pickerLocationId || undefined,
          targetClassId: selectedClassId,
        }),
        ...(targetType === MessageTargetType.STUDENTS && {
          targetLocationId: pickerLocationId || undefined,
          targetStudentIds: selectedStudentIds,
        }),
      };

      const result = await sendMessage(payload).unwrap();
      const count = result.recipientCount;
      showSuccess(`Announcement sent to ${count} recipient${count === 1 ? '' : 's'}.`);

      // Reset form
      setTitle('');
      setBody('');
      setRepliesAllowed(false);
      setTargetType(MessageTargetType.LOCATION);
      setSelectedLocationId(effectiveLocationId || '');
      setSelectedClassId('');
      setSelectedStudentIds([]);
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to send announcement. Please try again.');
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="messages-compose-form">
        {/* ── Target selection ───────────────────────────────────────────── */}
        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-2">Send to</legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {visibleTargets.map(({ value, label }) => (
              <label
                key={value}
                data-testid={`messages-target-${value}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  targetType === value
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="targetType"
                  value={value}
                  checked={targetType === value}
                  onChange={() => handleTargetChange(value)}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        {/* ── Conditional pickers ────────────────────────────────────────── */}

        {/* LOCATION picker — only for org-level users; otherwise the active location is used */}
        {targetType === MessageTargetType.LOCATION && isOrgLevel && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <select
              data-testid="messages-location-picker"
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">Select a location…</option>
              {locations.map((loc: Location) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Location hint for non-org-level users with LOCATION target */}
        {targetType === MessageTargetType.LOCATION && !isOrgLevel && activeLocationName && (
          <p className="text-sm text-gray-500">
            Sending to all parents and staff at <strong>{activeLocationName}</strong>.
          </p>
        )}

        {/* CLASS/STUDENTS — location picker for org-level users if no location selected */}
        {(targetType === MessageTargetType.CLASS || targetType === MessageTargetType.STUDENTS) &&
          isOrgLevel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="messages-class-location-picker"
                value={selectedLocationId}
                onChange={(e) => {
                  setSelectedLocationId(e.target.value);
                  setSelectedClassId('');
                  setSelectedStudentIds([]);
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Select a location…</option>
                {locations.map((loc: Location) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        {/* CLASS picker */}
        {targetType === MessageTargetType.CLASS && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Class <span className="text-red-500">*</span>
            </label>
            <select
              data-testid="messages-class-picker"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              disabled={!pickerLocationId}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {pickerLocationId ? 'Select a class…' : 'Select a location first…'}
              </option>
              {classes.map((cls: Class) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* STUDENTS picker */}
        {targetType === MessageTargetType.STUDENTS && pickerLocationId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Students <span className="text-red-500">*</span>
            </label>
            <StudentPicker
              locationId={pickerLocationId}
              selectedStudentIds={selectedStudentIds}
              onChange={setSelectedStudentIds}
            />
          </div>
        )}

        {targetType === MessageTargetType.STUDENTS && !pickerLocationId && isOrgLevel && (
          <p className="text-sm text-gray-400">Select a location above to pick students.</p>
        )}

        {/* ── Message fields ─────────────────────────────────────────────── */}
        <div>
          <label htmlFor="compose-title" className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="compose-title"
            type="text"
            data-testid="messages-compose-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. School trip reminder"
            required
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="compose-body" className="block text-sm font-medium text-gray-700 mb-1">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="compose-body"
            data-testid="messages-compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your announcement here…"
            required
            rows={5}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid="compose-replies-allowed"
            checked={repliesAllowed}
            onChange={(e) => setRepliesAllowed(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Parents can reply
        </label>

        {/* ── Submit ─────────────────────────────────────────────────────── */}
        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="messages-compose-submit"
            disabled={isSending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending…' : 'Send announcement'}
          </button>
        </div>
      </form>
      <ConfirmDialog
        isOpen={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmSend}
        title="Send announcement?"
        message={`Send "${title}" to ${buildTargetDescription()}?`}
        confirmLabel="Send"
        cancelLabel="Cancel"
        variant="default"
        isLoading={isSending}
      />
    </>
  );
};

export default ComposeAnnouncementForm;
